// Chata Ižipovce, bilingual content (Slovak + English)
// Real content from chatymara.com

// Self-hosted under /media (was the old GoHighLevel CDN, which this site no
// longer depends on). The ids are the original filenames, kept so the mapping
// back to the source photos stays obvious.
const IMG = (id) => `/media/${id}.webp`;

// Hero: the chalet beneath the Chočské vrchy ridge (self-hosted, matches /gallery/og.jpg).
export const HERO_IMAGE = "/gallery/hero.webp";

// ============================================================
// Google Business Profile / Maps
// Coordinates and CID come from the live GBP listing ("Chata Ižipovce",
// Ižipovce 70). Keep GEO in sync with scripts/prerender.js and public/index.html.
// ============================================================
export const GEO = { lat: 49.14537, lng: 19.489334 };
const GBP_CID = "15845581619663394850";

// Canonical GBP listing (photos, reviews, opening info).
export const MAPS_URL = `https://maps.google.com/?cid=${GBP_CID}`;
// Turn-by-turn navigation straight to the cottage.
export const MAPS_DIRECTIONS_URL = `https://www.google.com/maps/dir/?api=1&destination=${GEO.lat}%2C${GEO.lng}`;
// Public listing carrying the verified guest reviews shown in Testimonials.
export const REVIEWS_URL = "https://www.megaubytovanie.sk/chata-izipovce";

// Keyless embed, loaded only after the visitor opts in (see Location.jsx).
export const mapsEmbedUrl = (lang, label) =>
  `https://www.google.com/maps?q=${GEO.lat},${GEO.lng}(${encodeURIComponent(label)})&z=15&hl=${lang}&output=embed`;

// ============================================================
// Content dictionaries: content.sk and content.en
// ============================================================
const content = {
  sk: {
    lang: "sk",
    // Site umbrella brand (the domain identity). The property itself stays
    // "Chata Ižipovce" in brand/schema. Locations sit under Chaty Mara.
    site: { name: "Chaty Mara", domain: "chatymara.com" },
    brand: {
      name: "Chata Ižipovce",
      tagline: "Útulná chata v Liptove pri Liptovskej Mare",
      region: "Ižipovce, Liptovský Mikuláš",
      phone: "+421 918 677 279",
      phoneHref: "tel:+421918677279",
      email: "info@chatymara.com",
      emailHref: "mailto:info@chatymara.com",
      address: { line1: "Ižipovce 70", city: "032 23 Ižipovce", country: "Slovensko" },
      mapsUrl: MAPS_URL,
      directionsUrl: MAPS_DIRECTIONS_URL,
    },
    nav: [
      { label: "Úvod", href: "#top" },
      { label: "Ubytovanie", href: "#ubytovanie" },
      { label: "Cenník", href: "#cennik" },
      { label: "Galéria", href: "#galeria" },
      { label: "Recenzie", href: "#recenzie" },
      { label: "Atrakcie", href: "#atrakcie" },
      { label: "Lokalita", href: "#lokalita" },
      { label: "FAQ", href: "#faq" },
      { label: "Kontakt", href: "#kontakt" },
    ],
    hero: {
      kicker: "Ubytovanie • Liptovská Mara • Liptov",
      title: "Chaty Mara",
      subtitle:
        "Ubytovanie pri Liptovskej Mare. Naša Chata Ižipovce je útulná chata s krbom v tichej dedinke pod Chočskými vrchmi. Prenajímame ju vždy celú, len pre vás.",
      primaryCta: "Rezervovať pobyt",
      secondaryCta: "Zobraziť ubytovanie",
      trustLine: "Ižipovce 70 • Liptovský Mikuláš • Slovensko",
      imageAlt: "Chata Ižipovce v lete, v pozadí hory nad Liptovom",
    },
    ui: {
      reserve: "Rezervovať",
      callNow: "Zavolať teraz",
      openMap: "Otvoriť v mape",
      moreInfo: "Viac informácií",
      switchLang: "EN",
      submitInquiry: "Odoslať správu",
      submitting: "Odosielam...",
      thankYou: "Ďakujeme! Ozveme sa vám čo najskôr.",
      thankYouError: "Nepodarilo sa odoslať. Skúste to znova alebo nás kontaktujte priamo.",
      requiredError: "Vyplňte prosím meno a e-mail.",
      langToggleLabel: "Prepnúť jazyk",
      showMap: "Zobraziť mapu",
      mapPrivacyNote: "Mapa sa načíta z Google Máp až po kliknutí.",
      directions: "Navigovať",
      viewOnGoogle: "Zobraziť na Google",
    },
    trust: [
      { icon: "map-pin", label: "Blízko Liptovskej Mary" },
      { icon: "flame", label: "Krb & Wi-Fi zdarma" },
      { icon: "snow", label: "Celoročný pobyt" },
      { icon: "utensils", label: "Plne vybavená kuchyňa" },
      { icon: "car", label: "Parkovanie pri chate" },
    ],
    intro: {
      kicker: "Ubytovanie",
      title: "Izby a dispozícia",
      p1: "Chata má tri spálne, obývačku s krbom, kuchyňu s jedálenským kútom a kúpeľňu. Prenajímame ju vždy celú, takže priestor patrí len vašej rodine alebo partii.",
      p2: "V obývačke je pohodlná sedačka, krb a televízor so satelitnými kanálmi. Wi-Fi funguje v celej chate a je zdarma.",
    },
    cottages: [
      {
        slug: "spalne",
        name: "Spálne",
        tagline: "Kvalitný spánok s výhľadom na Liptov",
        description:
          "Spálne majú kvalitné matrace, čistú posteľnú bielizeň a dosť miesta na batožinu. Z okien vidno okolité kopce.",
        priceFrom: "Kvalitný oddych",
        chips: ["Kvalitné matrace", "Čistá bielizeň", "Veľké okná", "Výhľad na Liptov"],
        image: IMG("67ff637b71384b7af3b9d3bd"),
      },
      {
        slug: "kuchyna",
        name: "Kuchyňa a jedálenský kút",
        tagline: "Priestor pre spoločne strávený čas",
        description:
          "Kuchyňa má sporák, rúru, chladničku, mikrovlnku, rýchlovarnú kanvicu a kompletný riad. Pri jedálenskom stole sa v pohode navečeria celá partia.",
        priceFrom: "Plné vybavenie",
        chips: ["Sporák & rúra", "Chladnička", "Mikrovlnka", "Kompletný riad"],
        image: IMG("67ff637b71384b1e04b9d3ba"),
      },
      {
        slug: "kupelna",
        name: "Kúpeľňa",
        tagline: "Čistý a udržiavaný priestor",
        description:
          "Kúpeľňa so sprchovacím kútom, WC, umývadlom a neustálym prívodom teplej vody. Samozrejmosťou sú uteráky a základné toaletné potreby.",
        priceFrom: "Čistota a pohodlie",
        chips: ["Sprchovací kút", "WC", "Umývadlo", "Uteráky v cene"],
        image: IMG("67ff637b71384b7b67b9d3bc"),
      },
    ],
    amenitiesSection: {
      kicker: "Vybavenie",
      title: "Všetko, čo potrebujete pre pohodový pobyt",
      subtitle:
        "Chata je zariadená v tradičnom štýle, ale nič vám v nej nebude chýbať.",
    },
    amenities: [
      { icon: "wifi", label: "Wi-Fi zdarma" },
      { icon: "flame", label: "Krb" },
      { icon: "tv", label: "Satelitná TV" },
      { icon: "utensils", label: "Vybavená kuchyňa" },
      { icon: "grill", label: "Vonkajšie sedenie s grilom" },
      { icon: "car", label: "Parkovanie" },
      { icon: "bath", label: "Vybavená kúpeľňa" },
      { icon: "mountain", label: "Výhľad na Liptov" },
    ],
    whyUs: [
      {
        title: "Blízko Liptovskej Mary",
        body:
          "K vode je to od nás pár minút autom. Dá sa tam kúpať, požičať si loď alebo si len večer vyjsť na breh.",
      },
      {
        title: "Ideálne po celý rok",
        body:
          "V zime vás zahreje krb a vykurovací systém, v lete zostáva chata príjemne chladná. Skvelé miesto na pobyt v každom ročnom období.",
      },
      {
        title: "Domáca atmosféra",
        body:
          "Krb, ticho a domáca atmosféra, k tomu Wi-Fi, satelitná TV a vybavená kuchyňa.",
      },
    ],
    locationsSection: {
      kicker: "Naše lokality",
      title: "Kde nás nájdete",
      subtitle:
        "Každá lokalita má vlastnú stránku s tým, čo je okolo, ako sa tam dostanete a čo sa oplatí vidieť. Ďalšie lokality pribúdajú.",
      cards: [
        {
          name: "Chata Ižipovce",
          badge: "Ižipovce • pri Liptovskej Mare",
          body: "Útulná chata s krbom v tichej dedinke pod Chočskými vrchmi, pár minút od Liptovskej Mary a Prosieckej doliny.",
          href: "/chata-izipovce",
          image: "/gallery/ext-06.webp",
          linkLabel: "Spoznať lokalitu",
        },
      ],
    },
    gallery: {
      kicker: "Galéria",
      title: "Prezrite si Chatu Ižipovce",
      subtitle:
        "Interiér, exteriér aj okolie. Fotky sú z chaty, nie z katalógu.",
      close: "Zavrieť",
      categories: {
        exterior: "Exteriér",
        interior: "Interiér",
        bedroom: "Spálňa",
        kitchen: "Kuchyňa",
        bathroom: "Kúpeľňa",
        surroundings: "Okolie",
      },
      photoAlt: (label, i) => `Chata Ižipovce, ${label.toLowerCase()} ${i}`,
      openAlt: (i, n) => `Otvoriť fotku ${i} z ${n}`,
    },
    attractionsSection: {
      kicker: "Objavte Liptov",
      title: "Aktivity a atrakcie v okolí",
      subtitle:
        "Z Ižipoviec máte na dosah Liptovskú Maru aj Prosieckú a Kvačiansku dolinu. Nižšie nájdete vzdialenosti na miesta, kam chodia naši hostia najčastejšie.",
    },
    localGuide: {
      kicker: "Sprievodca okolím",
      title: "Čo všetko máte na dosah",
      subtitle:
        "Vzdialenosti sú merané autom z Ižipoviec. Vyberte si kategóriu podľa toho, aký deň plánujete.",
      allLabel: "Všetko",
      disclaimer:
        "Vzdialenosti sú orientačné (cesta autom). Otváracie hodiny a ceny si prosím overte priamo u prevádzkovateľa.",
      moreLabel: "Sprievodca ubytovaním pri Liptovskej Mare →",
      moreHref: "/chata-pri-liptovskej-mare",
      categories: [
        { id: "priroda", label: "Príroda a turistika" },
        { id: "voda", label: "Voda a pláže" },
        { id: "wellness", label: "Aquaparky a wellness" },
        { id: "zima", label: "Zima a lyžovanie" },
        { id: "kultura", label: "História a kultúra" },
        { id: "deti", label: "S deťmi" },
      ],
      places: [
        {
          name: "Babyland vo Villa Betula",
          category: "deti",
          distance: "4 km",
          body: "Detský areál v Liptovskej Sielnici s krytou hernou halou, trampolínami, zvieracou farmou a jazdou na poníkoch. Najbližšia atrakcia a jasná voľba, keď prší.",
          href: "https://www.villabetula.sk/",
        },
        {
          name: "Prosiecka dolina",
          category: "priroda",
          distance: "5 km",
          body: "Krasová tiesňava a národná prírodná rezervácia hneď za susednou dedinou. Trasa má rebríky a reťaze, takže je vhodná zhruba od 6 rokov. Počítajte 2 až 3 hodiny.",
          href: "https://www.visitliptov.sk/trasa/okruh-prosieckou-a-kvacianskou-dolinou/",
        },
        {
          name: "Marina Liptov, Bobrovník",
          category: "voda",
          distance: "6 km",
          body: "Najbližší prístup k Liptovskej Mare. Zátoka s plážou, požičovňa lodí a paddleboardov, wellness. Pokojnejšia alternatíva k Trnovcu.",
          href: "https://marinaliptov.sk/",
        },
        {
          name: "Liptovský hrad",
          category: "kultura",
          distance: "7 km",
          body: "Zrúcanina najvyššie položeného hradu na Slovensku (999 m). Prístupná len pešo, voľne a zadarmo, s kruhovým výhľadom na celý Liptov.",
          href: "",
        },
        {
          name: "Archeoskanzen Havránok",
          category: "kultura",
          distance: "9 km",
          body: "Keltské hradisko na ostrohu priamo nad Liptovskou Marou s rekonštrukciami obydlí. Sezónne, zvyčajne od mája do októbra.",
          href: "https://www.liptovskemuzeum.sk/expozicia/havranok/",
        },
        {
          name: "Pláž Liptovský Trnovec",
          category: "voda",
          distance: "9 km",
          body: "Hlavná organizovaná pláž na Liptovskej Mare s plavčíkmi, občerstvením a vodnými športmi. Letná sezóna, psy na pláž nesmú.",
          href: "https://www.liptovskytrnovec.eu/obec/plazove-kupalisko/",
        },
        {
          name: "Kvačianska dolina a mlyny Oblazy",
          category: "priroda",
          distance: "10 km",
          body: "Dve zrekonštruované drevené vodné mlyny na konci nenáročnej prechádzky bez rebríkov. Najlepšia rodinná trasa v okolí. Východisko Kvačany.",
          href: "https://www.visitliptov.sk/trasa/okruh-prosieckou-a-kvacianskou-dolinou/",
        },
        {
          name: "Vodný park Bešeňová",
          category: "wellness",
          distance: "12 km",
          body: "Termálne kúpalisko na geotermálnej vode s vonkajšími bazénmi, saunovým svetom a travertínovými terasami. Otvorené celoročne.",
          href: "https://www.besenova.com/",
        },
        {
          name: "Aquapark Tatralandia",
          category: "wellness",
          distance: "12 km",
          body: "Najväčší aquapark na Slovensku s celoročnou termálnou vodou, tobogánmi a krytou halou Tropical Paradise. Otvorené celoročne.",
          href: "https://www.tatralandia.sk/",
        },
        {
          name: "Liptovský Mikuláš",
          category: "kultura",
          distance: "15 km",
          body: "Najbližšie mesto. Námestie, reštaurácie, nákupy a Múzeum Janka Kráľa s expozíciou o zatopenej Liptovskej Mare. Dobrý tip na daždivý deň.",
          href: "https://www.visitliptov.sk/",
        },
        {
          name: "Termálny prameň Kalameny",
          category: "voda",
          distance: "16 km",
          body: "Voľne prístupné prírodné termálne jazierko na lúke, zadarmo a celoročne. Krásne najmä na jeseň a v zime.",
          href: "",
        },
        {
          name: "Lúčanský vodopád",
          category: "priroda",
          distance: "17 km",
          body: "Široký travertínový vodopád pár minút chôdze od cesty v kúpeľnej obci Lúčky. Minimum námahy, veľký zážitok aj s malými deťmi.",
          href: "",
        },
        {
          name: "Demänovská ľadová jaskyňa",
          category: "priroda",
          distance: "22 km",
          body: "Jaskyňa v Demänovskej doline s prehliadkou okolo 45 minút. Otvorená len od polovice mája do polovice septembra, v pondelok zatvorené. Teplota do 3 °C.",
          href: "https://www.ssj.sk/",
        },
        {
          name: "Demänovská jaskyňa slobody",
          category: "priroda",
          distance: "24 km",
          body: "Najnavštevovanejšia jaskyňa na Slovensku s kvapľovou výzdobou a podzemnou riekou. Otvorená celoročne okrem pondelkov, rezervácie sa neprijímajú.",
          href: "https://www.ssj.sk/",
        },
        {
          name: "Ružomberok a Malinô Brdo",
          category: "zima",
          distance: "25 km",
          body: "Lanovka z Hrabova nad Ružomberok. V zime lyžovanie, v lete bike park, kolobežky a rodinný chodník. Parkovanie zdarma.",
          href: "https://skipark.sk/",
        },
        {
          name: "Stanišovská jaskyňa",
          category: "deti",
          distance: "27 km",
          body: "Jaskyňa v Jánskej doline bez elektrického osvetlenia, každý návštevník dostane čelovku. Otvorená celoročne aj v pondelok, vhodná pre deti.",
          href: "https://www.podzemneatrakcieliptov.sk/sk/stanisovska-jaskyna",
        },
        {
          name: "Žiarska dolina",
          category: "priroda",
          distance: "28 km",
          body: "Ľadovcová dolina v Západných Tatrách. K Žiarskej chate vedie 6 km asfaltky bez áut. Je to najjednoduchší spôsob, ako zažiť veľhory s rodinou.",
          href: "https://ziarskachata.eu/",
        },
        {
          name: "Veľký Choč",
          category: "priroda",
          distance: "28 km",
          body: "Vápencový vrchol (1 611 m) s jedným z najkrajších výhľadov na Tatry. Náročný výstup s prevýšením nad 1 000 m, počítajte 5 až 6 hodín.",
          href: "",
        },
        {
          name: "Jasná a Chopok",
          category: "zima",
          distance: "29 km",
          body: "Najväčšie lyžiarske stredisko na Slovensku. V lete vyveziete lanovkami až na Chopok (2 024 m) bez jediného kroku do kopca.",
          href: "https://www.jasna.sk/",
        },
        {
          name: "Vlkolínec (UNESCO)",
          category: "kultura",
          distance: "29 km",
          body: "Živá dedinka 45 zrubových domov v zozname UNESCO nad Ružomberkom. Samotná dedina je prístupná celoročne a zadarmo, expozície sú sezónne.",
          href: "https://www.vlkolinec.sk/",
        },
        {
          name: "Múzeum liptovskej dediny Pribylina",
          category: "kultura",
          distance: "34 km",
          body: "Skanzen so stavbami zachránenými z obcí zatopených Liptovskou Marou vrátane gotického kostola. Otvorené celoročne.",
          href: "https://www.liptovskemuzeum.sk/expozicia/pribylina",
        },
        {
          name: "Oravský hrad",
          category: "kultura",
          distance: "46 km",
          body: "Jeden z najpôsobivejších hradov na Slovensku na 112 m vysokej skale nad Oravou. Celodenný výlet, otvorený celoročne s meniacimi sa hodinami.",
          href: "https://www.oravskemuzeum.sk/",
        },
      ],
    },
    attractions: [
      {
        name: "Liptovská Mara",
        distance: "0,5 km",
        body: "Kúpanie, opaľovanie, vodné športy ako člnkovanie, paddleboard či rybolov.",
        image: IMG("67ffafc4768a58beb0670bb2"),
        href: "https://www.liptovmara.sk/",
      },
      {
        name: "Prosiecka a Kvačianska dolina",
        distance: "turistický raj",
        body: "Malebné turistické trasy s vodopádmi, tiesňavami a historickými vodnými mlynmi.",
        image: IMG("67ffb0672e5c0a7cda616925"),
        href: "https://www.visitliptov.sk/trasa/okruh-prosieckou-a-kvacianskou-dolinou/",
      },
      {
        name: "Babyland vo Villa Betula",
        distance: "pre deti",
        body: "Detský areál so ZOO kútikom, trampolínami a jazdou na poníkoch.",
        image: IMG("67ffb129f502145b293b4331"),
        href: "https://www.villabetula.sk/sk/exterierove-ihrisko",
      },
    ],
    location: {
      kicker: "Lokalita",
      title: "Srdce Liptova na dosah",
      subtitle:
        "Sme v Ižipovciach v okrese Liptovský Mikuláš, medzi Nízkymi a Západnými Tatrami. K Liptovskej Mare je to pár minút autom.",
      addressLabel: "Adresa",
      contactLabel: "Kontaktné údaje",
      distancesLabel: "Vzdialenosti",
      maraLinkLabel: "Viac o chate pri Liptovskej Mare",
      maraLinkHref: "/chata-pri-liptovskej-mare",
      distances: [
        { name: "Liptovská Mara", value: "0,5 km" },
        { name: "Liptovský Mikuláš", value: "~15 min autom" },
        { name: "Prosiecka a Kvačianska dolina", value: "v okolí" },
      ],
    },
    pricing: {
      kicker: "Cenník",
      title: "Sezónne ceny za celú chatu",
      subtitle:
        "Ceny sú za celú chatu za jednu noc. Konečnú cenu vám radi upresníme na základe termínu a počtu hostí.",
      disclaimer:
        "K cene za ubytovanie sa pripočíta záverečné upratovanie 60 € za pobyt. Presnú kalkuláciu vám radi pošleme.",
      perNight: "€ / noc",
      minStayLabel: "Minimálny pobyt",
      seasons: [
        { name: "Zimná sezóna", period: "4. 1. až 31. 3.", price: "250", minNights: "2 noci", featured: false },
        { name: "Letná mimosezóna", period: "1. 4. až 27. 6.", price: "180", minNights: "2 noci", featured: false },
        { name: "Veľkonočný pobyt", period: "3. 4. až 6. 4.", price: "200", minNights: "2 noci", featured: false },
        { name: "Letná sezóna", period: "27. 6. až 31. 8.", price: "225", minNights: "2 noci", featured: true },
        { name: "Zimná mimosezóna", period: "1. 9. až 22. 12.", price: "180", minNights: "2 noci", featured: false },
        { name: "Vianočný pobyt", period: "23. 12. až 25. 12.", price: "280", minNights: "2 noci", featured: false },
        { name: "Silvester", period: "26. 12. až 3. 1.", price: "300", minNights: "3 noci", featured: false },
      ],
      ctaLabel: "Rezervovať pobyt",
      askLabel: "Získať presnú ponuku",
    },
    reviewsSection: {
      kicker: "Recenzie",
      title: "Čo hovoria naši hostia",
      subtitle:
        "Hodnotenia sú overené. Napísali ich hostia, ktorí u nás naozaj bývali, cez portál megaubytovanie.sk.",
      sourceLabel: "Overené hodnotenie na megaubytovanie.sk",
      sourceCta: "Zobraziť všetky hodnotenia",
      ratingLabel: "z 10",
    },
    reviews: [
      {
        name: "Lukáš H.",
        party: "Rodina s malými deťmi",
        stay: "August 2025",
        rating: "10",
        text:
          "Sme Vám veľmi vďační za dôveru a za poskytnutie domčeka. Boli sme nadmieru spokojní. Postele boli pohodlné, spalo sa nám všetkým výborne. Nemáme žiadne výhrady, práve naopak. Domček je nádherný, krásne zariadený a útulný. Obdivovali sme aj okolie. Naozaj sme boli očarení a všetko nám bohate stačilo.",
      },
      {
        name: "Henrieta M.",
        party: "Skupina",
        stay: "Silvester 2025",
        rating: "9,9",
        text: "Prostredie a útulnosť chaty, milí majitelia.",
      },
      {
        name: "Andrii M.",
        party: "Rodina so staršími deťmi",
        stay: "Apríl 2026",
        rating: "10",
        text: "Poloha, vybavenie a celkový víkend.",
      },
    ],
    faq: {
      kicker: "Časté otázky",
      title: "Všetko, čo potrebujete vedieť",
      subtitle: "Odpovede na najčastejšie otázky o rezervácii, vybavení a okolí.",
      items: [
        {
          q: "Kde sa Chata Ižipovce nachádza?",
          a: "Chata Ižipovce sa nachádza na okraji obce Ižipovce v okrese Liptovský Mikuláš, len pár minút od brehov Liptovskej Mary. Presná adresa: Ižipovce 70, 032 23 Ižipovce.",
        },
        {
          q: "Čo je zahrnuté v ubytovaní?",
          a: "V cene je priestranná obývacia izba s krbom a satelitnou televíziou, plne vybavená kuchyňa, kúpeľňa so sprchovacím kútom a bezplatné vysokorýchlostné Wi-Fi. K dispozícii je aj vykurovací systém pre zimné obdobie; v lete zostáva chata príjemne chladná. K cene za ubytovanie sa pripočíta záverečné upratovanie 60 € za pobyt.",
        },
        {
          q: "Aké aktivity nás čakajú v okolí?",
          a: "Na Liptovskej Mare (0,5 km) sa dá kúpať a športovať na vode. Prosiecka a Kvačianska dolina sú na turistiku popri vodopádoch a starých vodných mlynoch. S deťmi býva jasnou voľbou Babyland vo Villa Betula, so ZOO kútikom a trampolínami.",
        },
        {
          q: "Je chata vhodná aj v zime?",
          a: "Áno, Chata Ižipovce je ideálna počas celého roka. V chladných mesiacoch vás zahreje krb a vykurovací systém, v lete zostáva chata príjemne chladná.",
        },
        {
          q: "Sú povolené domáce zvieratá?",
          a: "Nie, domáce zvieratá bohužiaľ neprijímame.",
        },
        {
          q: "Aký je minimálny počet nocí?",
          a: "Minimálny pobyt sú 2 noci, počas silvestrovského obdobia 3 noci.",
        },
        {
          q: "Ste chatymara.com, nie ste Chaty Mara v Liptovskom Trnovci?",
          a: "Naša doména chatymara.com sa číta „Chaty Mara“ a je to internetové meno našej chaty. Samotná chata sa volá Chata Ižipovce a stojí v dedinke Ižipovce na pokojnejšej strane Liptovskej Mary. S rezortom Chaty Mara v Liptovskom Trnovci ani s penziónom Chata Mara nemáme nič spoločné.",
        },
        {
          q: "Ako môžem rezervovať pobyt?",
          a: "Rezerváciu môžete spraviť cez formulár na tejto stránke, e-mailom na info@chatymara.com alebo telefonicky na +421 918 677 279. Odpovieme vám čo najskôr.",
        },
        {
          q: "Aká je kapacita chaty?",
          a: "Chata ponúka niekoľko spální s kvalitnými matracmi, plne vybavenú kuchyňu s jedálenským kútom a vybavenú kúpeľňu. Presnú kapacitu radi upresníme pri rezervácii podľa vašich potrieb.",
        },
      ],
    },
    contact: {
      dates: {
        label: "Termín pobytu",
        placeholder: "Vyberte termín v kalendári",
        pickCheckout: "vyberte odchod",
        clear: "Zrušiť",
        legend: "Prečiarknuté dni sú už obsadené.",
        unavailable: "Tento termín je už obsadený. Vyberte prosím iný.",
      },
      kicker: "Kontaktujte nás",
      title: "Rezervujte si pobyt v Chate Ižipovce",
      subtitle:
        "Ak máte otázky ohľadom ubytovania, voľných termínov či atrakcií v okolí, ozvite sa nám. Odpovieme vám čo najskôr.",
      directTitle: "Kontaktujte nás priamo",
      quickCallKicker: "Rýchla rezervácia",
      quickCallTitle: "Radšej zavoláte?",
      quickCallBody:
        "Zavolajte nám priamo a radi vám poradíme s termínmi aj tipmi na výlety v okolí.",
      form: {
        name: "Meno a priezvisko *",
        namePh: "Ján Novák",
        email: "E-mail *",
        emailPh: "jan@email.sk",
        phone: "Telefón",
        phonePh: "+421 900 000 000",
        persons: "Počet osôb",
        personsPh: "4",
        dates: "Termín",
        datesPh: "napr. 10.7. až 14.7.2025",
        message: "Poznámka",
        messagePh: "Špeciálne požiadavky, otázky...",
        gdprNote:
          "Odoslaním súhlasíte so spracovaním osobných údajov na účely rezervácie.",
      },
      addressLabels: { phone: "Telefón", email: "E-mail", address: "Adresa" },
    },
    footer: {
      description:
        "Útulná chata v Liptove pri Liptovskej Mare. Obývačka s krbom, vybavená kuchyňa a pohodlné spálne. Na internete nás nájdete ako chatymara.com, teda Chaty Mara.",
      navLabel: "Navigácia",
      contactLabel: "Kontakt",
      rights: "Všetky práva vyhradené.",
      legalLine: "Chaty Mara · chatymara.com · Chata Ižipovce",
      strap: "Chata Ižipovce • chata pri Liptovskej Mare • Liptov • Slovensko",
      pagesLabel: "Sprievodcovia",
      pages: [
        { label: "Chata pri Liptovskej Mare", href: "/chata-pri-liptovskej-mare" },
        { label: "Dedinka Ižipovce", href: "/chata-izipovce" },
      ],
    },
    seo: {
      title: "Chaty Mara: ubytovanie pri Liptovskej Mare | Chata Ižipovce, Liptov",
      description:
        "Ubytovanie pri Liptovskej Mare. Celú Chatu Ižipovce máte len pre seba: krb, vybavená kuchyňa, Wi-Fi. Rezervácia priamo u majiteľa, bez provízií.",
      keywords:
        "chaty Mara, chata Mara, Liptov Mara, ubytovanie Liptovská Mara, chaty na Liptovskej Mare, chata pri Liptovskej Mare, chata na Mare, ubytovanie Liptov, chata Liptov s krbom, Chata Ižipovce, chatymara",
    },
  },

  en: {
    lang: "en",
    site: { name: "Chaty Mara", domain: "chatymara.com" },
    brand: {
      name: "Chata Ižipovce",
      tagline: "A cosy chalet in Liptov near Liptovská Mara lake",
      region: "Ižipovce, Liptovský Mikuláš, Slovakia",
      phone: "+421 918 677 279",
      phoneHref: "tel:+421918677279",
      email: "info@chatymara.com",
      emailHref: "mailto:info@chatymara.com",
      address: { line1: "Ižipovce 70", city: "032 23 Ižipovce", country: "Slovakia" },
      mapsUrl: MAPS_URL,
      directionsUrl: MAPS_DIRECTIONS_URL,
    },
    nav: [
      { label: "Home", href: "#top" },
      { label: "Rooms", href: "#ubytovanie" },
      { label: "Rates", href: "#cennik" },
      { label: "Gallery", href: "#galeria" },
      { label: "Reviews", href: "#recenzie" },
      { label: "Nearby", href: "#atrakcie" },
      { label: "Location", href: "#lokalita" },
      { label: "FAQ", href: "#faq" },
      { label: "Contact", href: "#kontakt" },
    ],
    hero: {
      kicker: "Stays • Liptovská Mara • Liptov",
      title: "Chaty Mara",
      subtitle:
        "Accommodation at Liptovská Mara. Our Chata Ižipovce is a cosy cottage with a fireplace in a quiet village beneath the Chočské hills. We rent it whole, to one group at a time.",
      primaryCta: "Book your stay",
      secondaryCta: "View accommodation",
      trustLine: "Ižipovce 70 • Liptovský Mikuláš • Slovakia",
      imageAlt: "Chata Ižipovce in summer with the mountains above Liptov behind it",
    },
    ui: {
      reserve: "Book",
      callNow: "Call now",
      openMap: "Open in maps",
      moreInfo: "More info",
      switchLang: "SK",
      submitInquiry: "Send inquiry",
      submitting: "Sending...",
      thankYou: "Thank you! We'll get back to you shortly.",
      thankYouError: "Could not send. Please try again or contact us directly.",
      requiredError: "Please fill in your name and email.",
      langToggleLabel: "Switch language",
      showMap: "Show map",
      mapPrivacyNote: "The map loads from Google Maps only after you click.",
      directions: "Get directions",
      viewOnGoogle: "View on Google",
    },
    trust: [
      { icon: "map-pin", label: "Near Liptovská Mara" },
      { icon: "flame", label: "Fireplace & free Wi-Fi" },
      { icon: "snow", label: "Year-round stays" },
      { icon: "utensils", label: "Fully equipped kitchen" },
      { icon: "car", label: "On-site parking" },
    ],
    intro: {
      kicker: "Accommodation",
      title: "Rooms & layout",
      p1: "The cottage has three bedrooms, a living room with a fireplace, a kitchen with a dining corner and a bathroom. We rent it whole, so the space belongs to your family or group alone.",
      p2: "The living room has a comfortable sofa, a fireplace and a TV with satellite channels. Wi-Fi works throughout the cottage and is free.",
    },
    cottages: [
      {
        slug: "spalne",
        name: "Bedrooms",
        tagline: "Quality sleep with views over Liptov",
        description:
          "The bedrooms have good mattresses, fresh linen and room for luggage. The windows look out on the surrounding hills.",
        priceFrom: "Restful comfort",
        chips: ["Quality mattresses", "Fresh linen", "Large windows", "Liptov views"],
        image: IMG("67ff637b71384b7af3b9d3bd"),
      },
      {
        slug: "kuchyna",
        name: "Kitchen & dining",
        tagline: "Space for shared meals and long evenings",
        description:
          "The kitchen has a cooker, oven, fridge, microwave, kettle and a full set of cookware. The dining table seats the whole group comfortably.",
        priceFrom: "Fully equipped",
        chips: ["Cooker & oven", "Fridge", "Microwave", "Full cookware"],
        image: IMG("67ff637b71384b1e04b9d3ba"),
      },
      {
        slug: "kupelna",
        name: "Bathroom",
        tagline: "A clean, well-kept space",
        description:
          "Bathroom with a shower, toilet, washbasin and a constant supply of hot water. Towels and basic toiletries are included as standard.",
        priceFrom: "Clean and comfortable",
        chips: ["Shower", "Toilet", "Washbasin", "Towels included"],
        image: IMG("67ff637b71384b7b67b9d3bc"),
      },
    ],
    amenitiesSection: {
      kicker: "Amenities",
      title: "Everything you need for a relaxing stay",
      subtitle:
        "The cottage is furnished in a traditional style, but nothing you need is missing.",
    },
    amenities: [
      { icon: "wifi", label: "Free Wi-Fi" },
      { icon: "flame", label: "Fireplace" },
      { icon: "tv", label: "Satellite TV" },
      { icon: "utensils", label: "Equipped kitchen" },
      { icon: "grill", label: "Outdoor seating with grill" },
      { icon: "car", label: "Parking" },
      { icon: "bath", label: "Well-equipped bathroom" },
      { icon: "mountain", label: "Views over Liptov" },
    ],
    whyUs: [
      {
        title: "Near Liptovská Mara",
        body:
          "The water is a few minutes away by car. You can swim there, rent a boat, or just walk the shore in the evening.",
      },
      {
        title: "Ideal year-round",
        body:
          "In winter the fireplace and heating keep you warm; in summer the chalet stays pleasantly cool. A great base in every season.",
      },
      {
        title: "A homely feel",
        body:
          "A fireplace, quiet and a homely feel, plus Wi-Fi, satellite TV and an equipped kitchen.",
      },
    ],
    locationsSection: {
      kicker: "Our locations",
      title: "Where to find us",
      subtitle:
        "Each location has its own page covering the surroundings, how to get there and what is worth seeing. More locations are on the way.",
      cards: [
        {
          name: "Chata Ižipovce",
          badge: "Ižipovce • near Liptovská Mara",
          body: "A cosy cottage with a fireplace in a quiet village beneath the Chočské hills, minutes from Liptovská Mara and the Prosiecka Valley.",
          href: "/en/chata-izipovce",
          image: "/gallery/ext-06.webp",
          linkLabel: "Explore the location",
        },
      ],
    },
    gallery: {
      kicker: "Gallery",
      title: "Take a look inside Chata Ižipovce",
      subtitle:
        "Inside, outside and the country around. These are photos of the cottage, not stock images.",
      close: "Close",
      categories: {
        exterior: "Exterior",
        interior: "Interior",
        bedroom: "Bedroom",
        kitchen: "Kitchen",
        bathroom: "Bathroom",
        surroundings: "Surroundings",
      },
      photoAlt: (label, i) => `Chata Ižipovce, ${label.toLowerCase()} ${i}`,
      openAlt: (i, n) => `Open photo ${i} of ${n}`,
    },
    attractionsSection: {
      kicker: "Discover Liptov",
      title: "Activities & attractions nearby",
      subtitle:
        "From Ižipovce you can reach Liptovská Mara and the Prosiecka and Kvačianska valleys easily. Below are the distances to the places our guests visit most.",
    },
    localGuide: {
      kicker: "Local guide",
      title: "Everything within easy reach",
      subtitle:
        "Driving distances from Ižipovce. Pick a category based on the kind of day you are planning.",
      allLabel: "Everything",
      disclaimer:
        "Distances are approximate driving distances. Please check opening hours and prices with each operator.",
      moreLabel: "Your guide to staying near Liptovská Mara →",
      moreHref: "/en/liptovska-mara-cottage",
      categories: [
        { id: "priroda", label: "Nature & hiking" },
        { id: "voda", label: "Water & beaches" },
        { id: "wellness", label: "Aquaparks & spa" },
        { id: "zima", label: "Winter & skiing" },
        { id: "kultura", label: "History & culture" },
        { id: "deti", label: "With kids" },
      ],
      places: [
        {
          name: "Babyland vo Villa Betula",
          category: "deti",
          distance: "4 km",
          body: "Children's resort in Liptovská Sielnica with an indoor play hall, trampolines, an animal farm and pony rides. Your nearest attraction and the obvious rainy-day answer.",
          href: "https://www.villabetula.sk/",
        },
        {
          name: "Prosiecka Valley",
          category: "priroda",
          distance: "5 km",
          body: "A karst gorge and national nature reserve just past the neighbouring village. The trail has ladders and chains, so roughly age 6 and up. Allow 2 to 3 hours.",
          href: "https://www.visitliptov.sk/trasa/okruh-prosieckou-a-kvacianskou-dolinou/",
        },
        {
          name: "Marina Liptov, Bobrovník",
          category: "voda",
          distance: "6 km",
          body: "The closest access to Liptovská Mara: a sheltered bay with a beach, boat and paddleboard rental and a wellness centre. Calmer than Trnovec.",
          href: "https://marinaliptov.sk/",
        },
        {
          name: "Liptov Castle ruins",
          category: "kultura",
          distance: "7 km",
          body: "The ruins of the highest-situated castle in Slovakia (999 m). Reached on foot only, freely accessible and free of charge, with a panorama over all of Liptov.",
          href: "",
        },
        {
          name: "Havránok Archaeological Museum",
          category: "kultura",
          distance: "9 km",
          body: "A Celtic hillfort on a headland directly above Liptovská Mara, with reconstructed dwellings. Seasonal, usually May to October.",
          href: "https://www.liptovskemuzeum.sk/expozicia/havranok/",
        },
        {
          name: "Liptovský Trnovec beach",
          category: "voda",
          distance: "9 km",
          body: "The main organised beach on Liptovská Mara, with lifeguards, refreshments and watersports. Summer season; dogs are not allowed on the beach.",
          href: "https://www.liptovskytrnovec.eu/obec/plazove-kupalisko/",
        },
        {
          name: "Kvačianska Valley & Oblazy mills",
          category: "priroda",
          distance: "10 km",
          body: "Two restored wooden watermills at the end of an easy, ladder-free walk. The best family route nearby. Start from Kvačany.",
          href: "https://www.visitliptov.sk/trasa/okruh-prosieckou-a-kvacianskou-dolinou/",
        },
        {
          name: "Bešeňová Water Park",
          category: "wellness",
          distance: "12 km",
          body: "A thermal park on geothermal water, with outdoor pools, a sauna world and travertine terraces. Open all year.",
          href: "https://www.besenova.com/",
        },
        {
          name: "Aquapark Tatralandia",
          category: "wellness",
          distance: "12 km",
          body: "Slovakia's largest aquapark: year-round thermal water, slides and the covered Tropical Paradise hall. Open all year.",
          href: "https://www.tatralandia.sk/",
        },
        {
          name: "Liptovský Mikuláš",
          category: "kultura",
          distance: "15 km",
          body: "Your nearest town: the main square, restaurants, shops and the Janko Kráľ Museum, with a display on the flooded Liptovská Mara villages. A good rainy-day option.",
          href: "https://www.visitliptov.sk/",
        },
        {
          name: "Kalameny thermal spring",
          category: "voda",
          distance: "16 km",
          body: "A free, natural open-air thermal pond in a meadow, accessible all year. Loveliest in autumn and winter.",
          href: "",
        },
        {
          name: "Lúčky Waterfall",
          category: "priroda",
          distance: "17 km",
          body: "A wide travertine waterfall a few minutes' walk from the road in the spa village of Lúčky. Minimal effort, big reward, even with small children.",
          href: "",
        },
        {
          name: "Demänovská Ice Cave",
          category: "priroda",
          distance: "22 km",
          body: "A cave in the Demänová Valley with a roughly 45-minute tour. Open only from mid-May to mid-September, closed on Mondays. Temperature below 3 °C.",
          href: "https://www.ssj.sk/",
        },
        {
          name: "Demänovská Cave of Liberty",
          category: "priroda",
          distance: "24 km",
          body: "Slovakia's most visited cave, with stalactite formations and an underground river. Open all year except Mondays; no reservations taken.",
          href: "https://www.ssj.sk/",
        },
        {
          name: "Ružomberok & Malinô Brdo",
          category: "zima",
          distance: "25 km",
          body: "A gondola from Hrabovo above Ružomberok: skiing in winter, a bike park, mountain carts and a family trail in summer. Free parking.",
          href: "https://skipark.sk/",
        },
        {
          name: "Stanišovská Cave",
          category: "deti",
          distance: "27 km",
          body: "A cave in the Jánska Valley with no electric lighting, so every visitor gets a headlamp. Open year-round including Mondays, and good with children.",
          href: "https://www.podzemneatrakcieliptov.sk/sk/stanisovska-jaskyna",
        },
        {
          name: "Žiarska Valley",
          category: "priroda",
          distance: "28 km",
          body: "A glacial valley in the Western Tatras. A 6 km car-free asphalt road leads to Žiarska hut. It is the easiest way to experience the high mountains as a family.",
          href: "https://ziarskachata.eu/",
        },
        {
          name: "Veľký Choč",
          category: "priroda",
          distance: "28 km",
          body: "A limestone peak (1,611 m) with one of the finest views of the Tatras. A demanding climb of over 1,000 m of ascent. Allow 5 to 6 hours.",
          href: "",
        },
        {
          name: "Jasná a Chopok",
          category: "zima",
          distance: "29 km",
          body: "Slovakia's largest ski resort. In summer the cable cars carry you all the way to Chopok (2,024 m) without a single step uphill.",
          href: "https://www.jasna.sk/",
        },
        {
          name: "Vlkolínec (UNESCO)",
          category: "kultura",
          distance: "29 km",
          body: "A living village of 45 log houses on the UNESCO list, above Ružomberok. The village itself is open year-round and free; the exhibitions are seasonal.",
          href: "https://www.vlkolinec.sk/",
        },
        {
          name: "Museum of the Liptov Village, Pribylina",
          category: "kultura",
          distance: "34 km",
          body: "An open-air museum of buildings rescued from the villages flooded by Liptovská Mara, including a Gothic church. Open all year.",
          href: "https://www.liptovskemuzeum.sk/expozicia/pribylina",
        },
        {
          name: "Orava Castle",
          category: "kultura",
          distance: "46 km",
          body: "One of Slovakia's most striking castles, on a 112 m rock above the river Orava. A full-day trip; open all year with month-by-month hours.",
          href: "https://www.oravskemuzeum.sk/",
        },
      ],
    },
    attractions: [
      {
        name: "Liptovská Mara Lake",
        distance: "0.5 km",
        body: "Swimming, sunbathing and water sports such as boating, paddleboarding and fishing.",
        image: IMG("67ffafc4768a58beb0670bb2"),
        href: "https://www.liptovmara.sk/",
      },
      {
        name: "Prosiecka & Kvačianska Valley",
        distance: "hiker's haven",
        body: "Scenic trails past waterfalls, gorges and historic water mills.",
        image: IMG("67ffb0672e5c0a7cda616925"),
        href: "https://www.visitliptov.sk/trasa/okruh-prosieckou-a-kvacianskou-dolinou/",
      },
      {
        name: "Babyland vo Villa Betula",
        distance: "for kids",
        body: "A family park with a petting zoo corner, trampolines and pony rides.",
        image: IMG("67ffb129f502145b293b4331"),
        href: "https://www.villabetula.sk/sk/exterierove-ihrisko",
      },
    ],
    location: {
      kicker: "Location",
      title: "The heart of Liptov within reach",
      subtitle:
        "We are in Ižipovce, in the Liptovský Mikuláš district, between the Low and Western Tatras. Liptovská Mara is a few minutes away by car.",
      addressLabel: "Address",
      contactLabel: "Contact details",
      distancesLabel: "Distances",
      maraLinkLabel: "More about the cottage near Liptovská Mara",
      maraLinkHref: "/en/liptovska-mara-cottage",
      distances: [
        { name: "Liptovská Mara", value: "0.5 km" },
        { name: "Liptovský Mikuláš", value: "~15 min by car" },
        { name: "Prosiecka & Kvačianska Valley", value: "in the area" },
      ],
    },
    pricing: {
      kicker: "Rates",
      title: "Seasonal pricing for the whole chalet",
      subtitle:
        "Prices are for the entire chalet per night. We'll gladly confirm the final quote based on your dates and party size.",
      disclaimer:
        "Final cleaning of €60 per stay is added to the accommodation price. Ask us and we will send you an exact quote.",
      perNight: "€ / night",
      minStayLabel: "Minimum stay",
      seasons: [
        { name: "Winter season", period: "4 Jan to 31 Mar", price: "250", minNights: "2 nights", featured: false },
        { name: "Summer off-season", period: "1 Apr to 27 Jun", price: "180", minNights: "2 nights", featured: false },
        { name: "Easter stay", period: "3 to 6 Apr", price: "200", minNights: "2 nights", featured: false },
        { name: "Summer season", period: "27 Jun to 31 Aug", price: "225", minNights: "2 nights", featured: true },
        { name: "Winter off-season", period: "1 Sep to 22 Dec", price: "180", minNights: "2 nights", featured: false },
        { name: "Christmas stay", period: "23 to 25 Dec", price: "280", minNights: "2 nights", featured: false },
        { name: "New Year's Eve", period: "26 Dec to 3 Jan", price: "300", minNights: "3 nights", featured: false },
      ],
      ctaLabel: "Book your stay",
      askLabel: "Get an exact quote",
    },
    reviewsSection: {
      kicker: "Reviews",
      title: "What our guests say",
      subtitle:
        "Verified reviews from guests who actually stayed with us, collected through megaubytovanie.sk. Translated from Slovak.",
      sourceLabel: "Verified review on megaubytovanie.sk",
      sourceCta: "See all reviews",
      ratingLabel: "of 10",
    },
    reviews: [
      {
        name: "Lukáš H.",
        party: "Family with small children",
        stay: "August 2025",
        rating: "10",
        text:
          "We are very grateful for your trust and for letting us stay in the cottage. We were more than satisfied. The beds were comfortable and we all slept wonderfully. We have no complaints at all, quite the opposite. The cottage is beautiful, lovely furnished and cosy. We admired the surroundings too. We were genuinely enchanted, and had everything we could have needed.",
      },
      {
        name: "Henrieta M.",
        party: "Group",
        stay: "New Year 2025",
        rating: "9.9",
        text: "The setting and the cosiness of the cottage, and lovely owners.",
      },
      {
        name: "Andrii M.",
        party: "Family with older children",
        stay: "April 2026",
        rating: "10",
        text: "The location, the facilities and the whole weekend.",
      },
    ],
    faq: {
      kicker: "FAQ",
      title: "Everything you need to know",
      subtitle: "Answers to the most common questions about booking, amenities and the area.",
      items: [
        {
          q: "Where is Chata Ižipovce located?",
          a: "Chata Ižipovce is on the edge of the village of Ižipovce in Liptovský Mikuláš district, just minutes from the shores of Liptovská Mara. Exact address: Ižipovce 70, 032 23 Ižipovce, Slovakia.",
        },
        {
          q: "What is included in the stay?",
          a: "A spacious living room with a fireplace and satellite TV, a fully equipped kitchen, a bathroom with a shower and free high-speed Wi-Fi. Central heating keeps it warm in winter, and the chalet stays pleasantly cool in summer. Final cleaning of €60 per stay is added to the accommodation price.",
        },
        {
          q: "What activities are there nearby?",
          a: "You can swim and do water sports on Liptovská Mara (0.5 km). The Prosiecka and Kvačianska valleys are for hiking past waterfalls and old mills. With children, the usual choice is Babyland at Villa Betula, with its petting zoo and trampolines.",
        },
        {
          q: "Is the chalet suitable in winter?",
          a: "Yes, Chata Ižipovce is ideal all year round. The fireplace and heating keep you warm during cold months, and the chalet stays pleasantly cool in summer.",
        },
        {
          q: "Are pets allowed?",
          a: "No, unfortunately we cannot accept pets.",
        },
        {
          q: "What is the minimum stay?",
          a: "The minimum stay is 2 nights, or 3 nights over the New Year period.",
        },
        {
          q: "Are you the same place as the Chaty Mara resort in Liptovský Trnovec?",
          a: "Our domain chatymara.com reads \"Chaty Mara\" and that is the online name of our cottage. The cottage itself is Chata Ižipovce, in the village of Ižipovce on the calmer side of Liptovská Mara. We are not affiliated with the Chaty Mara resort in Liptovský Trnovec or with Chata Mara.",
        },
        {
          q: "How do I book a stay?",
          a: "You can book using the form on this page, by email at info@chatymara.com or by phone at +421 918 677 279. We'll reply as soon as possible.",
        },
        {
          q: "What is the capacity of the chalet?",
          a: "The chalet has several bedrooms with quality mattresses, a fully equipped kitchen with a dining area and a well-equipped bathroom. We'll be happy to confirm the exact capacity when you book, based on your needs.",
        },
      ],
    },
    contact: {
      dates: {
        label: "Stay dates",
        placeholder: "Pick your dates",
        pickCheckout: "pick check-out",
        clear: "Clear",
        legend: "Crossed-out days are already booked.",
        unavailable: "Those dates are already booked. Please pick another stay.",
      },
      kicker: "Contact",
      title: "Book your stay at Chata Ižipovce",
      subtitle:
        "Have questions about the stay, availability or things to do nearby? Get in touch and we'll respond as soon as we can.",
      directTitle: "Contact us directly",
      quickCallKicker: "Quick booking",
      quickCallTitle: "Prefer to call?",
      quickCallBody:
        "Call us directly and we'll gladly help with dates and tips for exploring the area.",
      form: {
        name: "Full name *",
        namePh: "John Smith",
        email: "Email *",
        emailPh: "john@email.com",
        phone: "Phone",
        phonePh: "+421 900 000 000",
        persons: "Guests",
        personsPh: "4",
        dates: "Dates",
        datesPh: "e.g. 10 Jul to 14 Jul 2025",
        message: "Message",
        messagePh: "Special requests, questions...",
        gdprNote:
          "By submitting, you agree to processing of your personal data for booking purposes.",
      },
      addressLabels: { phone: "Phone", email: "Email", address: "Address" },
    },
    footer: {
      description:
        "A cosy cottage in Liptov near Liptovská Mara. A living room with a fireplace, an equipped kitchen and comfortable bedrooms. Rented whole, to one group at a time.",
      navLabel: "Navigation",
      contactLabel: "Contact",
      rights: "All rights reserved.",
      legalLine: "Chaty Mara · chatymara.com · Chata Ižipovce",
      strap: "Chata Ižipovce • cottage near Liptovská Mara • Liptov • Slovakia",
      pagesLabel: "Guides",
      pages: [
        { label: "Cottage near Liptovská Mara", href: "/en/liptovska-mara-cottage" },
        { label: "The village of Ižipovce", href: "/en/chata-izipovce" },
      ],
    },
    seo: {
      title: "Chaty Mara: Liptovská Mara Accommodation | Chata Ižipovce, Liptov",
      description:
        "Accommodation at Liptovská Mara. The whole of Chata Ižipovce is yours: fireplace, full kitchen, Wi-Fi. Book directly with the owner, no commission.",
      keywords:
        "Chaty Mara, Chata Mara, Liptov Mara, Liptovská Mara accommodation, Liptovská Mara cottages, cottage near Liptovská Mara, chalet Liptov Slovakia, cottage Slovakia lake, Chata Ižipovce",
    },
  },
};

// Gallery photos (shared between languages)
// Gallery: category keys map to per-locale labels in content.*.gallery.categories.
// Summer exteriors are self-hosted (frontend/public/gallery); interiors keep the
// original high-res sources. The photo archive's interior shots were lower-
// resolution duplicates of these, so they were deliberately not re-added.
export const GALLERY = [
  { src: "/gallery/hero.webp", cat: "exterior" },
  { src: "/gallery/ext-01.webp", cat: "exterior" },
  { src: "/gallery/ext-02.webp", cat: "exterior" },
  { src: "/gallery/ext-03.webp", cat: "exterior" },
  { src: "/gallery/ext-04.webp", cat: "exterior" },
  { src: "/gallery/ext-05.webp", cat: "exterior" },
  { src: "/gallery/ext-06.webp", cat: "exterior" },
  { src: "/gallery/ext-07.webp", cat: "exterior" },
  { src: "/gallery/ext-08.webp", cat: "exterior" },
  { src: "/gallery/ext-09.webp", cat: "exterior" },
  { src: IMG("67ff637bc7a015af0add0306"), cat: "exterior" },
  { src: IMG("67ff637be174c3ebf8f6d10d"), cat: "interior" },
  { src: IMG("67ff637ae174c31473f6d10a"), cat: "interior" },
  { src: IMG("67ff637b71384b18c7b9d3bb"), cat: "interior" },
  { src: IMG("67ff637be174c30420f6d10f"), cat: "interior" },
  { src: IMG("67ff637b80d564e2f0f77ce8"), cat: "interior" },
  { src: IMG("67ff637b71384b1e04b9d3ba"), cat: "kitchen" },
  { src: IMG("67ff637b71384b7af3b9d3bd"), cat: "bedroom" },
  { src: IMG("67ff637a80d5647c15f77ce7"), cat: "bedroom" },
  { src: IMG("67ff637bc7a01579ebdd0305"), cat: "bedroom" },
  { src: IMG("67ff637b266b6f5cfa97f04b"), cat: "bedroom" },
  { src: IMG("67ff637b266b6ff20497f04a"), cat: "bedroom" },
  { src: IMG("67ff637bc7a0158330dd0307"), cat: "bedroom" },
  { src: IMG("67ff637b71384b7b67b9d3bc"), cat: "bathroom" },
  { src: IMG("67ff637be174c369aef6d10c"), cat: "bathroom" },
  { src: "/gallery/oko-01.webp", cat: "surroundings" },
  { src: "/gallery/oko-02.webp", cat: "surroundings" },
  { src: "/gallery/oko-03.webp", cat: "surroundings" },
  { src: "/gallery/oko-04.webp", cat: "surroundings" },
];

export default content;
